import { useEffect, useRef, useState } from 'react';

import { ReferenceTemplatePart, TemplateTestResult } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

/** Espera entre el último cambio de marcas y la prueba en vivo (FR-017). */
export const TEMPLATE_TEST_DEBOUNCE_MS = 300;

export interface DebouncedTemplateTest {
  /** Resultados de la última prueba vigente (undefined antes de la primera o sin prueba posible). */
  results?: TemplateTestResult[];
  /** Hay una prueba esperando el debounce o en curso. */
  loading: boolean;
  error?: unknown;
}

/**
 * Prueba en vivo de un formato de referencia (spec 009, US5): `POST .../test` 300 ms después del último cambio de las
 * partes o de los textos. Cada cambio invalida la solicitud anterior (número de solicitud), así que una respuesta vieja
 * nunca pisa a una nueva. Sin `enabled` (partes inválidas, sin textos o cerrado) no llama y limpia el resultado.
 */
export const useDebouncedTemplateTest = (
  parts: ReferenceTemplatePart[],
  texts: string[],
  onTest: ((parts: ReferenceTemplatePart[], texts: string[]) => Promise<TemplateTestResult[]>) | undefined,
  enabled: boolean
): DebouncedTemplateTest => {
  const [results, setResults] = useState<TemplateTestResult[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const request = useRef(0);
  const callback = useRef(onTest);
  callback.current = onTest;

  // Clave estable: el efecto corre sólo cuando cambia el contenido, no la identidad de los arreglos.
  const key = enabled && onTest && texts.length > 0 ? JSON.stringify([parts, texts]) : '';

  useEffect(() => {
    const requestId = ++request.current;
    if (!key) {
      setResults(undefined);
      setLoading(false);
      setError(undefined);
      return undefined;
    }
    setLoading(true);
    const [currentParts, currentTexts] = JSON.parse(key) as [ReferenceTemplatePart[], string[]];
    let current = true;
    const timer = window.setTimeout(() => {
      const test = callback.current;
      if (!test) return;
      test(currentParts, currentTexts)
        .then((value) => {
          if (!current || requestId !== request.current) return;
          setResults(value);
          setError(undefined);
        })
        .catch((failure: unknown) => {
          if (!current || requestId !== request.current) return;
          setError(failure);
        })
        .finally(() => {
          if (current && requestId === request.current) setLoading(false);
        });
    }, TEMPLATE_TEST_DEBOUNCE_MS);
    // Cambio de partes / textos o desmontaje: la respuesta en curso ya no actualiza el estado.
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [key]);

  return { results, loading, error };
};

export default useDebouncedTemplateTest;
